import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Lightbulb, ArrowRight, Target, Route } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { loadContent, saveLesson, getLesson } from "@/lib/offlineContent";
import { postOrQueue } from "@/lib/offlineSync";
import { useAuth } from "@/context/AuthContext";
import { GeneratingState } from "@/components/GeneratingState";
import { SpeakButton } from "@/components/SpeakButton";
import { MicButton } from "@/components/MicButton";
import { ExerciseRunner } from "@/components/exercises/ExerciseRunner";
import { Celebration } from "@/components/Celebration";
import { GrammarRules } from "@/components/lesson/GrammarRules";
import { SoundsSection } from "@/components/lesson/SoundsSection";
import { ScriptSection } from "@/components/lesson/ScriptSection";

const LearnView = ({ lesson, lang, onPractice }) => (
  <div className={`${lesson.topic.kind === "script" ? "max-w-5xl" : "max-w-3xl"} mx-auto animate-fade-up`}>
    <p className="caption">{lesson.language.name} · {lesson.level}{lesson.fromCache && <span data-testid="lesson-offline-copy" className="ml-2 text-slate-900"> · Offline copy</span>}</p>
    <h1 data-testid="lesson-title" className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1">{lesson.topic.title}</h1>
    <p className="text-slate-700 mt-4 text-lg leading-relaxed">{lesson.intro}</p>
    {lesson.grammar_tip && (
      <div className="mt-6 rounded-2xl bg-[hsl(var(--accent-blue-light))] p-5 flex gap-3">
        <Lightbulb className="text-[hsl(var(--accent-blue))] shrink-0" />
        <p className="text-slate-800 text-sm"><b>Tip:</b> {lesson.grammar_tip}</p>
      </div>
    )}
    {lesson.sounds?.length > 0 && <SoundsSection sounds={lesson.sounds} lang={lang} />}
    {lesson.topic.kind === "script" && <ScriptSection characters={lesson.characters} lang={lang} />}
    {lesson.grammar_rules?.length > 0 && <GrammarRules rules={lesson.grammar_rules} lang={lang} />}
    <h2 className="font-display text-xl font-bold text-slate-900 mt-10 mb-4">Key vocabulary</h2>
    <div className="grid sm:grid-cols-2 gap-3">
      {lesson.vocabulary.map((v, i) => (
        <div key={i} data-testid={`vocab-card-${i}`} className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
          <div className="flex-1 min-w-0">
            <p className="font-display text-xl font-bold text-slate-900">{v.word}</p>
            {v.romaji && <p className="text-xs text-[hsl(var(--accent-blue))] font-semibold">{v.romaji}</p>}
            <p className="text-sm text-slate-600">{v.translation}</p>
          </div>
          <SpeakButton text={v.word} lang={lang} testId={`vocab-speak-${i}`} />
          <MicButton text={v.word} romaji={v.romaji} lang={lang} testId={`vocab-mic-${i}`} />
        </div>
      ))}
    </div>
    <h2 className="font-display text-xl font-bold text-slate-900 mt-10 mb-4">Example sentences</h2>
    <div className="space-y-3">
      {lesson.examples.map((e, i) => (
        <div key={i} data-testid={`example-${i}`} className="rounded-2xl bg-white border-l-4 border-[hsl(var(--primary))] border-y border-r border-slate-200 p-4 flex gap-3 items-center">
          <div className="flex-1">
            <p className="font-semibold text-slate-900 text-lg">{e.text}</p>
            {e.romaji && <p className="text-xs text-[hsl(var(--accent-blue))] font-semibold">{e.romaji}</p>}
            <p className="text-sm text-slate-600">{e.translation}</p>
          </div>
          <SpeakButton text={e.text} lang={lang} testId={`example-speak-${i}`} />
          <MicButton text={e.text} romaji={e.romaji} lang={lang} testId={`example-mic-${i}`} />
        </div>
      ))}
    </div>
    <button data-testid="start-practice-button" onClick={onPractice} className="btn-primary w-full mt-10">
      Start practice ({lesson.exercises.length} exercises) <ArrowRight size={18} />
    </button>
  </div>
);

export default function Lesson() {
  const { lang, level, topic } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("learn");
  const [done, setDone] = useState(null);
  const mistakes = useRef([]);

  const load = useCallback(async () => {
    setError("");
    try {
      setLesson(await loadContent({ lang, level, topic, endpoint: "/lessons/generate", get: getLesson, save: saveLesson }));
    } catch (e) {
      setError(e.response ? errMsg(e) : e.message);
    }
  }, [lang, level, topic]);

  useEffect(() => { load(); }, [load]);

  const finish = async (correct, total) => {
    try {
      if (mistakes.current.length) await postOrQueue("review_add", { language: lang, items: mistakes.current });
      const data = await postOrQueue("lesson_complete", { language: lang, level, topic_id: topic, correct, total });
      const xp = data ? data.xp_earned : 10 + Math.min(correct, 10) * 5;
      setUser((u) => (data ? data.user : { ...u, xp: u.xp + xp }));
      setDone({ correct, total, xp, offline: !data });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!lesson) return <GeneratingState error={error} onRetry={load} />;
  return (
    <div className="px-4 sm:px-6 py-8">
      {phase === "learn" ? (
        <LearnView lesson={lesson} lang={lang} onPractice={() => setPhase("practice")} />
      ) : (
        <ExerciseRunner exercises={lesson.exercises} lang={lang} onFinish={finish} onResult={(ex, ok) => !ok && mistakes.current.push(ex)} onExit={() => setPhase("learn")} />
      )}
      <Celebration open={!!done} onClose={() => navigate(`/learn/${lang}`)} title="Practice complete!" xp={done?.xp}
        subtitle={done && `You got ${done.correct} of ${done.total} right.${done.offline ? " Saved on this device, it will sync when you're back online." : ""}${mistakes.current.length ? " Missed items were added to your review deck." : ""} Ready to prove it in the assessment?`}>
        <button data-testid="celebration-assessment-button" onClick={() => navigate(`/assessment/${lang}/${level}/${topic}`)} className="btn-primary"><Target size={18} /> Take the assessment</button>
        <button data-testid="celebration-map-button" onClick={() => navigate(`/learn/${lang}`)} className="btn-ghost"><Route size={18} /> Back to path</button>
      </Celebration>
    </div>
  );
}
